import {
  type AllReportStatus,
  formatPosition,
  type KeyInfoItem,
  REPORT_TYPE_KINDS,
  REPORT_TYPES,
  type ReportLocation,
  type ReportType,
  reportTypeOf,
  type VoyageReportKind,
} from '@felix/domain';
import { useTranslation } from 'react-i18next';
import { formatTime } from '../../format.js';
import { useNumber } from './numbers.js';

/** Seçenek listesinin çeviri yeri (domain `KeyInfoList`). */
const OPTION_KEYS: Record<string, string> = {
  loadCondition: 'voyage.event.options.loadConditions',
  waitingReason: 'voyage.event.options.reasons',
  nextOperation: 'voyage.sections.next.operations',
  dailyActivity: 'voyage.daily.activities',
  cargoOperation: 'voyage.event.options.cargoOperations',
};

/** Bir türü olan rapor türünde menüdeki ad, birden çok türü olanda kaydın kendi adı. */
export const SINGLE_KIND = new Set(
  REPORT_TYPES.filter((type) => REPORT_TYPE_KINDS[type].length === 1),
);

/**
 * Rapor satırlarının ekrandaki metinleri (All Reports, Voyage Details, Recent Events; ADR 0082
 * §6, §7): rapor türü, konum, Key Information ve durum. Değerler domain'den, metin dilden.
 */
export function useReportLabels() {
  const { t } = useTranslation();
  const n = useNumber();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`voyage.allReports.${key}`, options);

  const typeLabel = (kind: VoyageReportKind, type: ReportType = reportTypeOf(kind)) =>
    SINGLE_KIND.has(type) && type !== 'other' ? k(`types.${type}`) : t(`voyage.kinds.${kind}`);
  const position = (location: { lat: number | null; lon: number | null }) =>
    location.lat !== null && location.lon !== null
      ? `${formatPosition(location.lat, 'lat')}, ${formatPosition(location.lon, 'lon')}`
      : null;
  const locationLabel = (location: ReportLocation) => {
    switch (location.type) {
      case 'port':
      case 'area':
      case 'passage':
        return location.name;
      case 'sea':
      case 'anchorage': {
        const at = position(location);
        return at
          ? k(`locations.${location.type}At`, { position: at })
          : k(`locations.${location.type}`);
      }
      default:
        return '—';
    }
  };
  const keyValue = (item: KeyInfoItem) => {
    switch (item.type) {
      case 'number':
        return n.unit(item.value, item.unit, item.digits);
      case 'quantity':
        return `${n(item.value, 0)} ${t(`voyage.sections.cargo.units.${item.unit}`, { defaultValue: item.unit })}`;
      case 'time':
        return formatTime(item.at, 0);
      case 'option':
        return t(`${OPTION_KEYS[item.list]}.${item.value}`, { defaultValue: item.value });
      case 'text':
        return item.value;
      default:
        return '';
    }
  };
  const keyInfo = (items: readonly KeyInfoItem[]) =>
    items.map((item) => k(`keys.${item.key}`, { value: keyValue(item) })).join(' | ');
  const statusLabel = (status: AllReportStatus) => k(`statuses.${status}`);
  return { typeLabel, locationLabel, keyInfo, statusLabel };
}
